import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { LanguageSelect } from '../components/common/LanguageSelect';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorNotification } from '../components/common/ErrorNotification';
import { Modal } from '../components/common/Modal';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import config from '../config/config';
import { isSpeechSupported, getSpeechSettings, saveSpeechSettings, speak } from '../utils/speak';

const PREDEFINED_INTERESTS = [
  'Soccer', 'Music', 'Programming', 'Travel', 'Cooking',
  'Reading', 'Gaming', 'Art', 'Fitness', 'Photography',
  'Movies', 'Science', 'History', 'Fashion', 'Business'
];

/**
 * Profile page - allows user to edit name and interests
 */
const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();

  const [name, setName] = useState('');
  const [interestsList, setInterestsList] = useState([]);
  const [nativeLanguage, setNativeLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorNotification, setErrorNotification] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Pronunciation settings state
  const [speechSettings, setSpeechSettings] = useState(getSpeechSettings);
  const [speechSaved, setSpeechSaved] = useState(false);

  const handleSaveSpeechSettings = () => {
    saveSpeechSettings(speechSettings);
    setSpeechSaved(true);
    setTimeout(() => setSpeechSaved(false), 2000);
  };

  // Chat settings state
  const [chatSettings, setChatSettings] = useState(null);
  const [retentionDays, setRetentionDays] = useState(7);
  const [isSavingChatSettings, setIsSavingChatSettings] = useState(false);

  // Load current profile data
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);

        // Load profile and chat settings in parallel
        const [profileResponse, chatSettingsResponse] = await Promise.all([
          apiClient.getInterests(),
          apiClient.getChatSettings().catch(err => {
            console.warn('Failed to load chat settings:', err);
            return null; // Don't fail if chat settings not available
          })
        ]);

        if (profileResponse.exists) {
          setName(profileResponse.name || '');
          const raw = profileResponse.interests || '';
          setInterestsList(raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : []);
          setNativeLanguage(profileResponse.nativeLanguage || 'en');
        } else {
          // User doesn't exist in DB yet - use AuthContext data
          setName(user?.name || '');
          setInterestsList([]);
          setNativeLanguage('en');
        }

        // Set chat settings if available
        if (chatSettingsResponse) {
          setChatSettings(chatSettingsResponse);
          setRetentionDays(chatSettingsResponse.retentionDays || 7);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setErrorNotification('Failed to load profile data.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const handleInterestClick = (interest) => {
    if (interestsList.includes(interest)) return;
    const newList = [...interestsList, interest];
    if (newList.join(', ').length <= config.validation.maxInterestsLength) {
      setInterestsList(newList);
    }
  };

  const handleRemoveInterest = (interest) => {
    setInterestsList(interestsList.filter(i => i !== interest));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    } else if (name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!nativeLanguage) {
      newErrors.nativeLanguage = 'Native language is required';
    }

    if (interestsList.join(', ').length > config.validation.maxInterestsLength) {
      newErrors.interests = `Interests must be ${config.validation.maxInterestsLength} characters or less`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      setSuccessMessage('');

      await apiClient.updateProfile(name.trim(), interestsList.join(', '), nativeLanguage);

      // Update in-memory user so the header name reflects the change immediately
      updateUser({ name: name.trim() });

      setSuccessMessage('Profile updated successfully!');

      // Redirect to home after 1.5 seconds
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorNotification(
        error.response?.data?.error ||
        'Failed to update profile. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'delete') return;
    try {
      setIsDeleting(true);
      await apiClient.deleteAccount();
      await logout();
    } catch (error) {
      console.error('Error deleting account:', error);
      setErrorNotification(error.response?.data?.error || 'Failed to delete account. Please try again.');
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleUpdateChatSettings = async (e) => {
    e.preventDefault();

    try {
      setIsSavingChatSettings(true);
      await apiClient.updateChatSettings(retentionDays);

      // Update local state
      setChatSettings(prev => ({
        ...prev,
        retentionDays
      }));

      setSuccessMessage('Chat settings updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating chat settings:', error);
      setErrorNotification(
        error.response?.data?.error ||
        'Failed to update chat settings. Please try again.'
      );
    } finally {
      setIsSavingChatSettings(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto flex justify-center py-12">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h2>

        <form onSubmit={handleSubmit}>
          {/* Name Field */}
          <Input
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            maxLength={100}
            error={errors.name}
            showCharCount
          />

          {/* Native Language Field */}
          <LanguageSelect
            label="Native Language"
            value={nativeLanguage}
            onChange={(e) => setNativeLanguage(e.target.value)}
            error={errors.nativeLanguage}
            required
          />

          {/* Interests Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Interests</label>
            <p className="text-sm text-gray-600 mb-3">
              Your interests help us generate personalized vocabulary examples.
            </p>
            {interestsList.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {interestsList.map((interest) => (
                  <span
                    key={interest}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {interest}
                    <button
                      type="button"
                      onClick={() => handleRemoveInterest(interest)}
                      disabled={isSaving}
                      className="ml-1 text-blue-500 hover:text-blue-800 focus:outline-none leading-none"
                      aria-label={`Remove ${interest}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic mb-3">No interests added yet.</p>
            )}
            {errors.interests && <p className="text-sm text-red-500 mb-2">{errors.interests}</p>}
          </div>

          {/* Predefined Interest Buttons */}
          <div className="mb-6">
            <p className="text-sm font-medium text-gray-700 mb-2">Quick Add:</p>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_INTERESTS.map((interest) => (
                <button
                  key={interest}
                  type="button"
                  onClick={() => handleInterestClick(interest)}
                  className={`px-3 py-1 text-sm rounded-full transition-colors ${
                    interestsList.includes(interest)
                      ? 'bg-blue-100 text-blue-500 cursor-default'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  disabled={isSaving || interestsList.includes(interest)}
                >
                  {interestsList.includes(interest) ? interest : `+ ${interest}`}
                </button>
              ))}
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
              type="button"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>

      {/* Pronunciation Settings Section */}
      {isSpeechSupported() && (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Pronunciation</h2>
          <p className="text-sm text-gray-500 mb-6">Configure how words are read aloud.</p>

          {/* Accent */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-2">Accent</label>
            <select
              value={speechSettings.accent || 'en-US'}
              onChange={(e) => setSpeechSettings(s => ({ ...s, accent: e.target.value }))}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            >
              <option value="en-US">American English</option>
              <option value="en-GB">British English</option>
            </select>
          </div>

          {/* Speed */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">Speed</label>
              <span className="text-sm text-gray-500">{speechSettings.rate.toFixed(1)}×</span>
            </div>
            <input
              type="range"
              min="0.5" max="2" step="0.1"
              value={speechSettings.rate}
              onChange={(e) => setSpeechSettings(s => ({ ...s, rate: parseFloat(e.target.value) }))}
              className="w-full accent-primary-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>0.5× slow</span>
              <span>1× normal</span>
              <span>2× fast</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              type="button"
              onClick={() => speak('tomato')}
            >
              Test
            </Button>
            <Button type="button" onClick={handleSaveSpeechSettings}>
              {speechSaved ? 'Saved!' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {/* Chat Settings Section */}
      {chatSettings && (
        <div className="bg-white shadow rounded-lg p-6 mt-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Chat Settings</h2>

          <form onSubmit={handleUpdateChatSettings}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message Retention Period
              </label>

              <p className="text-sm text-gray-600 mb-3">
                How long should your chat messages be kept before automatic deletion?
              </p>

              {chatSettings.tier === 'free' ? (
                // Free tier - locked to 7 days
                <div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-gray-700">7 days (Free tier)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Upgrade to Premium to customize retention period up to 90 days
                  </p>
                </div>
              ) : (
                // Premium tier - dropdown selection
                <div>
                  <select
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(parseInt(e.target.value))}
                    className="block w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    disabled={isSavingChatSettings}
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days (Recommended)</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Messages older than this period will be automatically deleted
                  </p>
                </div>
              )}
            </div>

            {/* Save button for premium users */}
            {chatSettings.tier === 'premium' && (
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSavingChatSettings || retentionDays === chatSettings.retentionDays}
                >
                  {isSavingChatSettings ? 'Saving...' : 'Update Retention Period'}
                </Button>
              </div>
            )}
          </form>

          {/* Daily Usage Limits */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Usage Limits</h3>

            {chatSettings.usage && chatSettings.limits ? (
              <>
                {/* Usage indicators */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* User messages usage */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        Your Messages
                      </span>
                      <span className="text-sm text-gray-600">
                        {chatSettings.usage.userCharsRemaining.toLocaleString()} remaining
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          ((chatSettings.usage.userCharsUsed / chatSettings.limits.dailyUserChars) * 100) >= 90 ? 'bg-red-600' :
                          ((chatSettings.usage.userCharsUsed / chatSettings.limits.dailyUserChars) * 100) >= 75 ? 'bg-yellow-500' :
                          'bg-green-600'
                        }`}
                        style={{ width: `${Math.min((chatSettings.usage.userCharsUsed / chatSettings.limits.dailyUserChars) * 100, 100)}%` }}
                      />
                    </div>

                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {chatSettings.usage.userCharsUsed.toLocaleString()} / {chatSettings.limits.dailyUserChars.toLocaleString()} chars
                      </span>
                      <span className="text-xs text-gray-500">
                        {((chatSettings.usage.userCharsUsed / chatSettings.limits.dailyUserChars) * 100).toFixed(0)}% used
                      </span>
                    </div>
                  </div>

                  {/* AI responses usage */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">
                        AI Responses
                      </span>
                      <span className="text-sm text-gray-600">
                        {chatSettings.usage.aiCharsRemaining.toLocaleString()} remaining
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          ((chatSettings.usage.aiCharsUsed / chatSettings.limits.dailyAiChars) * 100) >= 90 ? 'bg-red-600' :
                          ((chatSettings.usage.aiCharsUsed / chatSettings.limits.dailyAiChars) * 100) >= 75 ? 'bg-yellow-500' :
                          'bg-green-600'
                        }`}
                        style={{ width: `${Math.min((chatSettings.usage.aiCharsUsed / chatSettings.limits.dailyAiChars) * 100, 100)}%` }}
                      />
                    </div>

                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-500">
                        {chatSettings.usage.aiCharsUsed.toLocaleString()} / {chatSettings.limits.dailyAiChars.toLocaleString()} chars
                      </span>
                      <span className="text-xs text-gray-500">
                        {((chatSettings.usage.aiCharsUsed / chatSettings.limits.dailyAiChars) * 100).toFixed(0)}% used
                      </span>
                    </div>
                  </div>
                </div>

                {/* Daily reset info */}
                <p className="text-xs text-gray-500">
                  Daily limits reset at midnight UTC
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">Loading usage data...</p>
            )}
          </div>

          {/* Tier info */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Current Plan</p>
                <p className={`text-lg font-bold ${chatSettings.tier === 'premium' ? 'text-purple-600' : 'text-gray-600'}`}>
                  {chatSettings.tier === 'premium' ? 'Premium' : 'Free'}
                </p>
              </div>
              {chatSettings.tier === 'free' && (
                <Button
                  variant="primary"
                  onClick={() => {
                    apiClient.logEvent('upgrade_to_premium_clicked', { source: 'profile_page' });
                    setShowUpgradeModal(true);
                  }}
                >
                  Upgrade to Premium
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Legal */}
      <div className="bg-white shadow rounded-lg p-6 mt-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Legal</h2>
        <div className="flex gap-4 mt-3">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
            Privacy Policy
          </a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
            Terms of Service
          </a>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white shadow rounded-lg p-6 mt-6 border border-red-100">
        <h2 className="text-xl font-bold text-red-600 mb-1">Danger Zone</h2>
        <p className="text-sm text-gray-500 mb-4">
          Permanently delete your account and all your data. This cannot be undone.
        </p>
        <button
          type="button"
          onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
          className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
        >
          Delete my account
        </button>
      </div>

      {/* Delete Account Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => !isDeleting && setShowDeleteModal(false)}
        title="Delete Account"
        size="sm"
      >
        <div className="py-2">
          <p className="text-gray-700 mb-2">
            This will permanently delete <strong>all your words, chat history, and profile data</strong>. There is no way to recover this.
          </p>
          <p className="text-sm text-gray-600 mb-4">
            Type <strong>delete</strong> to confirm:
          </p>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="delete"
            disabled={isDeleting}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
          />
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} type="button">
              Cancel
            </Button>
            <button
              type="button"
              onClick={handleDeleteAccount}
              disabled={deleteConfirmText !== 'delete' || isDeleting}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting ? 'Deleting...' : 'Delete my account'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Notification */}
      <ErrorNotification
        message={errorNotification}
        onClose={() => setErrorNotification(null)}
      />

      {/* Upgrade Coming Soon Modal */}
      <Modal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Premium Plan"
        size="sm"
      >
        <div className="text-center py-4">
          <div className="text-4xl mb-4">🚀</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon</h3>
          <p className="text-gray-600 mb-6">
            Premium features are not available yet. We're working on it and will notify you when it's ready!
          </p>
          <Button onClick={() => setShowUpgradeModal(false)}>Got it</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ProfilePage;
